import { useEffect, useMemo, useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type EditableTable =
  | "invoices"
  | "orders"
  | "subscriptions"
  | "profiles"
  | "billing_details";

export type FieldDef = {
  name: string;
  label: string;
  kind: "text" | "number" | "date" | "datetime" | "select" | "json" | "textarea" | "boolean";
  options?: Array<{ value: string; label: string }>;
  help?: string;
  /** Value shown but not editable (Stripe-owned). */
  locked?: boolean;
  full?: boolean;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  table: EditableTable;
  title: string;
  description?: string;
  fields: FieldDef[];
  record?: Record<string, unknown> | null;
  /** Values pre-filled when creating a new record. */
  defaults?: Record<string, unknown>;
  onSaved?: () => void;
};

const toInputValue = (kind: FieldDef["kind"], value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (kind === "json") return JSON.stringify(value, null, 2);
  if (kind === "datetime") {
    const d = new Date(String(value));
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  if (kind === "date") return String(value).slice(0, 10);
  if (kind === "boolean") return value === true ? "true" : "false";
  return String(value);
};

const RecordEditDialog = ({
  open,
  onOpenChange,
  table,
  title,
  description,
  fields,
  record,
  defaults,
  onSaved,
}: Props) => {
  const mode: "update" | "insert" = record?.id ? "update" : "insert";
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const initial = useMemo(() => {
    const base: Record<string, string> = {};
    fields.forEach((f) => {
      const src = record ? record[f.name] : defaults?.[f.name];
      base[f.name] = toInputValue(f.kind, src);
    });
    return base;
  }, [fields, record, defaults]);

  useEffect(() => {
    if (open) setValues(initial);
  }, [open, initial]);

  const setField = (name: string, value: string) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const save = async () => {
    const payload: Record<string, unknown> = {};
    for (const f of fields) {
      if (f.locked) continue;
      const raw = values[f.name] ?? "";
      const before = initial[f.name] ?? "";
      if (mode === "update" && raw === before) continue;
      if (raw === "") {
        payload[f.name] = null;
        continue;
      }
      payload[f.name] = raw;
    }
    if (Object.keys(payload).length === 0) {
      toast.info("Inget att spara");
      return;
    }
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-edit-record", {
        body: {
          table,
          action: mode,
          id: record?.id,
          values: payload,
        },
      });
      if (error) {
        // Edge function returns a JSON error body on 4xx.
        const ctx = (error as { context?: Response }).context;
        let msg = error.message;
        if (ctx && typeof ctx.json === "function") {
          try {
            const body = await ctx.json();
            if (body?.error) msg = body.error;
          } catch { /* keep original */ }
        }
        throw new Error(msg);
      }
      if ((data as { error?: string })?.error) {
        throw new Error((data as { error: string }).error);
      }
      toast.success(mode === "insert" ? "Posten skapades" : "Ändringarna sparades");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(`Kunde inte spara: ${e instanceof Error ? e.message : "okänt fel"}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description ??
              "Manuell ändring. Uppgifterna uppdateras bara hos oss – ingenting skickas till betalleverantören."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          {fields.map((f) => (
            <div key={f.name} className={f.full ? "md:col-span-2 space-y-1.5" : "space-y-1.5"}>
              <Label htmlFor={`field-${f.name}`} className="flex items-center gap-1.5">
                {f.label}
                {f.locked && <Lock className="h-3 w-3 text-muted-foreground" />}
              </Label>

              {f.kind === "select" ? (
                <Select
                  value={values[f.name] ?? ""}
                  onValueChange={(v) => setField(f.name, v)}
                  disabled={f.locked}
                >
                  <SelectTrigger id={`field-${f.name}`}>
                    <SelectValue placeholder="Välj…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(f.options ?? []).map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.kind === "json" || f.kind === "textarea" ? (
                <Textarea
                  id={`field-${f.name}`}
                  value={values[f.name] ?? ""}
                  onChange={(e) => setField(f.name, e.target.value)}
                  disabled={f.locked}
                  rows={f.kind === "json" ? 6 : 3}
                  className={f.kind === "json" ? "font-mono text-xs" : undefined}
                />
              ) : f.kind === "boolean" ? (
                <Select
                  value={values[f.name] ?? "false"}
                  onValueChange={(v) => setField(f.name, v)}
                  disabled={f.locked}
                >
                  <SelectTrigger id={`field-${f.name}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Ja</SelectItem>
                    <SelectItem value="false">Nej</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id={`field-${f.name}`}
                  type={
                    f.kind === "number"
                      ? "number"
                      : f.kind === "date"
                        ? "date"
                        : f.kind === "datetime"
                          ? "datetime-local"
                          : "text"
                  }
                  value={values[f.name] ?? ""}
                  onChange={(e) => setField(f.name, e.target.value)}
                  disabled={f.locked}
                />
              )}

              {f.help && <p className="text-xs text-muted-foreground">{f.help}</p>}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Avbryt
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {mode === "insert" ? "Skapa" : "Spara ändringar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecordEditDialog;
